import { API_BASE_URL } from '../config/backendConfig';

const CHATBOT_API = `${API_BASE_URL}/chatbot`;
const SESSION_STORAGE_KEY = 'yovo_chatbot_session_id';

/**
 * Get or create session ID
 * Persists in localStorage, cleared when chat is cleared
 */
function getSessionId() {
  try {
    return localStorage.getItem(SESSION_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

/**
 * Store session ID from backend response
 */
function setSessionId(sessionId) {
  if (sessionId) {
    try {
      localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    } catch { /* ignore storage errors */ }
  }
}

/**
 * Clear session ID (call when chat is cleared)
 */
export function clearChatbotSession() {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch { /* ignore storage errors */ }
}

/**
 * Get auth headers if user is logged in
 */
function getAuthHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
  } catch { /* ignore storage errors */ }
  return headers;
}

export const chatbotService = {
  sendMessage: async (message, conversationHistory = [], offTopicCount = 0) => {
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${CHATBOT_API}/message`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ message, conversationHistory, offTopicCount, sessionId }),
      });

      if (response.status === 429) {
        return { success: false, message: 'Rate limit exceeded. Please try again in a moment.', error: 'RATE_LIMIT' };
      }

      const data = await response.json();
      
      // Persist sessionId from response
      if (data.sessionId) {
        setSessionId(data.sessionId);
      }
      
      return data;
    } catch (error) {
      return {
        success: false,
        message: 'Sorry, I\'m having trouble connecting right now. Please try again later.',
        error: 'NETWORK_ERROR'
      };
    }
  },

  sendMessageStream: async (message, conversationHistory = [], onChunk, offTopicCount = 0) => {
    try {
      const sessionId = getSessionId();
      const response = await fetch(`${CHATBOT_API}/message/stream`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ message, conversationHistory, offTopicCount, sessionId }),
      });

      if (!response.ok) {
        if (response.status === 429) {
          return { success: false, message: 'Rate limit exceeded. Please try again in a moment.', error: 'RATE_LIMIT' };
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullMessage = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);

              if (data === '[DONE]') {
                return { success: true, message: fullMessage, usage: null };
              }

              try {
                const parsed = JSON.parse(data);
                
                // Persist sessionId from response
                if (parsed.sessionId) {
                  setSessionId(parsed.sessionId);
                }
                
                if (parsed.error) {
                  return { success: false, message: 'Yovo is temporarily unavailable. Please try again shortly.', error: parsed.error };
                }
                if (parsed.content) {
                  fullMessage = parsed.content;
                  onChunk(parsed.content);
                }
                // Return full response with all new fields including suggestedActions and offTopicCount
                if (parsed.actions || parsed.tripBrief || parsed.refinements || parsed.suggestedActions || parsed.done) {
                  return {
                    success: true,
                    message: fullMessage,
                    actions: parsed.actions || [],
                    suggestedActions: parsed.suggestedActions || null,
                    tripBrief: parsed.tripBrief || null,
                    refinements: parsed.refinements || null,
                    source: parsed.source || null,
                    offTopicCount: parsed.offTopicCount ?? 0,
                    intentCategory: parsed.intentCategory || null,
                    sessionId: parsed.sessionId || null,
                    usage: null
                  };
                }
              } catch (_) { /* skip invalid JSON lines */ }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      return { success: true, message: fullMessage, usage: null };
    } catch (error) {
      return {
        success: false,
        message: 'Sorry, I\'m having trouble connecting right now. Please try again later.',
        error: 'NETWORK_ERROR'
      };
    }
  },

  getTravelRecommendations: async (destination, preferences = {}) => {
    const message = `I'm planning a trip to ${destination}. Can you give me some travel recommendations? My preferences: ${JSON.stringify(preferences)}`;
    return chatbotService.sendMessage(message);
  },

  getFlightSearchHelp: async (query) => {
    const message = `I need help with flight search: ${query}`;
    return chatbotService.sendMessage(message);
  },

  getItinerarySuggestions: async (destination, duration, interests = []) => {
    const message = `I'm planning a ${duration} day trip to ${destination}. My interests are: ${interests.join(', ')}. Can you suggest an itinerary?`;
    return chatbotService.sendMessage(message);
  },

  getTravelTips: async (destination, travelType = 'general') => {
    const message = `What are some useful travel tips for ${destination}? I'm interested in ${travelType} travel.`;
    return chatbotService.sendMessage(message);
  },

  analyzeFlightOptions: async (flights) => {
    const message = `I have these flight options: ${JSON.stringify(flights)}. Can you help me analyze which one is the best choice and why?`;
    return chatbotService.sendMessage(message);
  },

  getWeatherAdvice: async (destination, travelDate) => {
    const message = `I'm traveling to ${destination} on ${travelDate}. What should I know about the weather and what should I pack?`;
    return chatbotService.sendMessage(message);
  },

  getBudgetTips: async (destination, budget) => {
    const message = `I'm planning a trip to ${destination} with a budget of ${budget}. What are some budget-friendly tips and suggestions?`;
    return chatbotService.sendMessage(message);
  }
};

export const fallbackResponses = {
  greeting: "Hello! I'm Yovo, your personal travel guide! 🌍 I can help you plan amazing itineraries, discover new destinations, and book flights. Where would you like to travel today?",
  flightSearch: "I can help you plan your trip and find flights! Tell me where you'd like to go, and I'll create a detailed itinerary for you.",
  noApiKey: "Yovo is temporarily unavailable. Please try again shortly.",
  rateLimit: "I'm getting a lot of travel requests right now! Please try again in a moment, or tell me about your dream destination.",
  error: "I'm having trouble connecting right now, but I can still help you plan your trip! Tell me where you'd like to travel.",
  travelTips: "Here are some travel planning tips: Research your destination's culture and customs, check visa requirements, book flights in advance for better prices, and always have travel insurance!",
  itinerary: "I love planning itineraries! Tell me your destination and how many days you have, and I'll create a detailed day-by-day plan with the best attractions, local experiences, and cultural insights."
};

export default chatbotService;
